import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  BEESWAX_CUSTOM_LIST_ITEMS_KEYS,
  BEESWAX_CUSTOM_LIST_KEYS,
} from "./beeswax.constant";
import {
  BeeswaxCustomListItemsKeys,
  BeeswaxCustomListKeys,
  ListApiResponse,
  ListType,
  ListV1ApiResponse,
} from "./beeswaxList.interface";
import {
  createBeeswaxList,
  createBulkListItem,
  getAllBeeswaxList,
  getBeeswaxList,
  getBeeswaxListActive,
  getBeeswaxListItems,
  updateBeeswaxList,
} from "@/apis/v1/beeswax";
import { AnyObjectSchema } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Lazy from "yup/lib/Lazy";
import beeswaxV2toV1 from "@/utils/beeswaxV2toV1";

export function useCreateOrEditForm({
  data,
  listType,
  isEdit = false,
  validationSchema,
}: {
  data?: any;
  listType?: ListType;
  isEdit?: boolean;
  validationSchema: AnyObjectSchema | Lazy<any, unknown>;
}) {
  const defaultValues = useDefaultValues({ data, listType });

  const rhForm = useForm<any>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  return { rhForm };
}

function useDefaultValues({ data, listType }: any) {
  return {
    id: data?.id ?? "",
    type: listType ?? data?.listType ?? null,
    alternativeId: data?.alternative_id ?? "",
    name: data?.name ?? "",
    active: data?.active ? "active" : "inactive",
  };
}

export function useAllBeeswaxList<TDATA>(
  args?: {
    queryParams?: Record<string, string>;
    options?: Record<any, any>;
  },
  key: BeeswaxCustomListKeys = "all"
) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery<TDATA>(
    BEESWAX_CUSTOM_LIST_KEYS[key](queryParams),
    getAllBeeswaxList,
    {
      meta: {
        queryParams: { ...queryParams },
      },
      ...options,
    }
  );
}

export function useActiveBeeswaxList(
  args?: {
    queryParams?: {
      id?: number;
      name?: string;
      type?: string;
    };
    options?: Record<any, any>;
  },
  key: BeeswaxCustomListKeys = "default"
) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery<ListApiResponse>(
    BEESWAX_CUSTOM_LIST_KEYS[key]({ ...queryParams, active: true }),
    getBeeswaxListActive,
    {
      meta: {
        queryParams: { ...queryParams },
      },
      ...options,
    }
  );
}
export function useBeeswaxList(
  args?: {
    queryParams?: {
      id?: number;
      name?: string;
      type?: string;
      active?: boolean;
    };
    options?: Record<any, any>;
  },
  key: BeeswaxCustomListKeys = "default"
) {
  const { queryParams = {}, options = {} } = args ?? {};
  return useQuery<ListV1ApiResponse>(
    BEESWAX_CUSTOM_LIST_KEYS[key](queryParams),
    getBeeswaxList,
    {
      meta: {
        queryParams: { ...queryParams },
      },
      ...options,
    }
  );
}

export function useCreateBeeswaxListMutation(args?: {
  options?: Record<any, any>;
}) {
  return useMutation(createBeeswaxList<any>, args?.options);
}

export function useUpdateBeeswaxListMutation(args?: {
  options?: Record<any, any>;
}) {
  return useMutation(updateBeeswaxList<any>, args?.options);
}

export function useCreateBulkListItemMutation() {
  return useMutation(createBulkListItem);
}

export function useUploadBulkListItem() {
  const {
    mutateAsync: createBulkListItem,
    isLoading,
    error,
  } = useCreateBulkListItemMutation();

  async function onHandleUploadFile(file: any, id: number) {
    if (file && id && !isLoading) {
      const formData = new FormData();
      formData.append("file", file, file.name.replace(/\s/g, ""));
      return await createBulkListItem({
        id,
        data: formData,
      });
    }
  }

  return {
    mutate: onHandleUploadFile,
    isLoading,
    error,
  };
}

export function useBeeswaxListItems<TDATA>(
  args: {
    queryParams: {
      id: number;
      name?: string;
      rows?: number;
      active?: boolean;
    };
    options?: Record<any, any>;
  },
  key: BeeswaxCustomListItemsKeys = "default"
) {
  const { queryParams, options = {} } = args ?? {};
  return useQuery<TDATA>(
    BEESWAX_CUSTOM_LIST_ITEMS_KEYS[key](queryParams),
    getBeeswaxListItems,
    {
      meta: {
        queryParams: { ...{ ...beeswaxV2toV1(queryParams, "list") } },
      },
      ...options,
    }
  );
}
